import { Permissions } from '@/constants/permissionEnums'
import { useTranslation } from '@/features/shared/composables/useTranslation'
import { PermissionType } from '@/features/users/types'
import { useAuthStore } from '@/store/auth'
import { computed } from 'vue'

const t = useTranslation()

export const usePermissions = () => {
  const authStore = useAuthStore()

  // Transform permissions into a Set for O(1) lookups
  const permissionSet = computed(() => {
    const set = new Set<string>()
    if (authStore.user) {
      for (const section of authStore.user.permissions) {
        for (const permission of section.permissions) {
          if (permission.has_permission) {
            set.add(permission.codename)
          }
        }
      }
    }
    return set
  })

  const hasPermission = (identifiers?: PermissionType | PermissionType[]) => {
    // If no identifiers are provided, the action is allowed by default
    if (!identifiers) return true

    // Convert identifiers to an array if it's not already
    const identifiersArray = Array.isArray(identifiers) ? identifiers : [identifiers]

    // Check if any of the identifiers are in the permission set
    return identifiersArray.some((identifier) => permissionSet.value.has(identifier))
  }

  // Precompute all permissions for easy access
  const permissions = computed(() => {
    return {
      account: {
        view: permissionSet.value.has(Permissions.CAN_VIEW_ACCOUNT),
        create: permissionSet.value.has(Permissions.CAN_CREATE_ACCOUNT),
        update: permissionSet.value.has(Permissions.CAN_UPDATE_ACCOUNT),
      },
      user: {
        delete: permissionSet.value.has(Permissions.CAN_DELETE_USER),
        deactivate: permissionSet.value.has(Permissions.CAN_DEACTIVATE_USER),
        activate: permissionSet.value.has(Permissions.CAN_ACTIVATE_USER),
        update: permissionSet.value.has(Permissions.CAN_UPDATE_USER),
        view: permissionSet.value.has(Permissions.CAN_VIEW_USER),
        create: permissionSet.value.has(Permissions.CAN_INVITE_USER),
      },
      invitation: {
        delete: permissionSet.value.has(Permissions.CAN_DELETE_INVITATION),
        view: permissionSet.value.has(Permissions.CAN_VIEW_INVITATIONS),
      },
      product: {
        view: permissionSet.value.has(Permissions.CAN_VIEW_PRODUCT),
        update: permissionSet.value.has(Permissions.CAN_UPDATE_PRODUCT),
      },
      business: {
        update: permissionSet.value.has(Permissions.CAN_UPDATE_BUSINESS),
      },
      listings: {
        view: permissionSet.value.has(Permissions.CAN_VIEW_LISTING),
        create: permissionSet.value.has(Permissions.CAN_CREATE_LISTING),
        update: permissionSet.value.has(Permissions.CAN_UPDATE_LISTING),
        pause: permissionSet.value.has(Permissions.CAN_PAUSE_LISTING),
        resume: permissionSet.value.has(Permissions.CAN_RESUME_LISTING),
      },
      listingLink: {
        view: permissionSet.value.has(Permissions.CAN_VIEW_LISTING_LINKS),
        create: permissionSet.value.has(Permissions.CAN_CREATE_LISTING_LINKS),
        update: permissionSet.value.has(Permissions.CAN_UPDATE_LISTING_LINKS),
        delete: permissionSet.value.has(Permissions.CAN_DELETE_LISTING_LINKS),
      },
      listingBrandStyles: {
        view: permissionSet.value.has(Permissions.CAN_VIEW_LISTING_BRAND_STYLES),
        update: permissionSet.value.has(Permissions.CAN_UPDATE_LISTING_BRAND_STYLES),
      },
      listingAnalytics: {
        view: permissionSet.value.has(Permissions.CAN_VIEW_LISTING_ANALYTICS),
        export: permissionSet.value.has(Permissions.CAN_EXPORT_LISTING_ANALYTICS),
      },
      dtc: {
        view: permissionSet.value.has(Permissions.CAN_VIEW_DTC_ACCOUNT),
        update: permissionSet.value.has(Permissions.CAN_UPDATE_DTC_ACCOUNT),
      },
      dtcProducts: {
        view: permissionSet.value.has(Permissions.CAN_VIEW_DTC_PRODUCTS),
        update: permissionSet.value.has(Permissions.CAN_UPDATE_DTC_PRODUCT),
        sync: permissionSet.value.has(Permissions.CAN_SYNC_DTC_PRODUCTS),
        delete: permissionSet.value.has(Permissions.CAN_DELETE_DTC_PRODUCT),
      },
      priceAlert: {
        view: permissionSet.value.has(Permissions.CAN_VIEW_PRICE_ALERT),
        update: permissionSet.value.has(Permissions.CAN_UPDATE_PRICE_ALERT),
        create: permissionSet.value.has(Permissions.CAN_CREATE_PRICE_ALERT),
        delete: permissionSet.value.has(Permissions.CAN_DELETE_PRICE_ALERT),
        viewSummary: permissionSet.value.has(Permissions.CAN_VIEW_PRICE_SUMMARY),
        viewProductSummary: permissionSet.value.has(Permissions.CAN_VIEW_PRODUCT_PRICE_SUMMARY),
        viewHistory: permissionSet.value.has(Permissions.CAN_VIEW_PRICE_HISTORY),
        viewProductHistory: permissionSet.value.has(Permissions.CAN_VIEW_PRODUCT_PRICE_HISTORY),
      },
      quarantine: {
        view: permissionSet.value.has(Permissions.CAN_VIEW_QUARANTINE),
        create: permissionSet.value.has(Permissions.CAN_CREATE_QUARANTINE),
        lift: permissionSet.value.has(Permissions.CAN_LIFT_QUARANTINE),
      },
    }
  })

  const getNoAccessMessage = (page: string) => {
    return t('no_page_view_permission', { page })
  }

  return { hasPermission, permissions, getNoAccessMessage }
}
